import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useParams } from 'react-router-dom';
import {
  Alert,
  AlertTitle,
  Fade,
} from '@mui/material';
import NotificationsActiveRoundedIcon from '@mui/icons-material/NotificationsActiveRounded';
import './ApplicationForm.css';

const SUCCESS_TOAST = (
  <>
    We appreciate the time and effort you put into it. Please send a brief
    message to <strong>hr@cofounderhubs.com</strong> including your full name
    and the position you applied for. This will help our hiring team quickly
    identify and review your application. Thank you once again for applying to
    Cofounder Hubs!
  </>
);

const ApplicationForm = () => {
  const { jobId } = useParams();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    currentLocation: '',
    linkedinUrl: '',
    githubUrl: '',
    yearsOfExperience: '',
    employmentType: {
      fullTime: false,
      partTime: false,
      contract: false
    },
    rateType: 'hourly',
    hourlyRate: '',
    monthlyRate: '',
    coverLetter: '',
    resume: null
  });

  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);

  useEffect(() => {
    if (!toastOpen) return undefined;
    const timer = setTimeout(() => setToastOpen(false), 14000);
    return () => clearTimeout(timer);
  }, [toastOpen]);

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) newErrors.fullName = 'Full name is required';
    if (!formData.email.trim()) newErrors.email = 'Email is required';
    if (!formData.phone.trim()) newErrors.phone = 'Phone is required';
    if (!formData.currentLocation.trim()) newErrors.currentLocation = 'Current location is required';
    
    // LinkedIn validation
    if (!formData.linkedinUrl.trim()) {
      newErrors.linkedinUrl = 'LinkedIn URL is required';
    } else if (!formData.linkedinUrl.includes('linkedin.com')) {
      newErrors.linkedinUrl = 'Please enter a valid LinkedIn URL';
    }
    
    // GitHub validation
    if (!formData.githubUrl.trim()) {
      newErrors.githubUrl = 'GitHub URL is required';
    } else if (!formData.githubUrl.includes('github.com')) {
      newErrors.githubUrl = 'Please enter a valid GitHub URL';
    }
    
    // Years of experience
    if (!formData.yearsOfExperience) {
      newErrors.yearsOfExperience = 'Years of experience is required';
    }
    
    // Employment type - optional now
    const hasEmploymentType = Object.values(formData.employmentType).some(v => v);
    if (!hasEmploymentType) {
      // Don't add error - it's optional now
    }
    
    // Rate validation - only hourly rate now
    if (!formData.hourlyRate) {
      newErrors.hourlyRate = 'Hourly rate is required';
    }
    
    // Resume validation
    if (!formData.resume) {
      newErrors.resume = 'Resume is required';
    } else if (!['application/pdf', 'application/msword', 
                  'application/vnd.openxmlformats-officedocument.wordprocessingml.document']
                  .includes(formData.resume.type)) {
      newErrors.resume = 'Resume must be PDF, DOC, or DOCX';
    } else if (formData.resume.size > 5 * 1024 * 1024) {
      newErrors.resume = 'Resume must be less than 5MB';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleEmploymentTypeChange = (type) => {
    setFormData(prev => ({
      ...prev,
      employmentType: {
        ...prev.employmentType,
        [type]: !prev.employmentType[type]
      }
    }));
    if (errors.employmentType) {
      setErrors(prev => ({
        ...prev,
        employmentType: ''
      }));
    }
  };

  const handleResumeChange = (e) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      resume: file
    }));
    if (errors.resume) {
      setErrors(prev => ({
        ...prev,
        resume: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setMessage('');

    try {
      // Create FormData for file upload
      const data = new FormData();
      data.append('jobId', jobId);
      data.append('fullName', formData.fullName);
      data.append('email', formData.email);
      data.append('phone', formData.phone);
      data.append('currentLocation', formData.currentLocation);
      data.append('linkedinUrl', formData.linkedinUrl);
      data.append('githubUrl', formData.githubUrl);
      data.append('yearsOfExperience', formData.yearsOfExperience);
      data.append('employmentType', JSON.stringify(formData.employmentType));
      data.append('rateType', formData.rateType);
      data.append('hourlyRate', formData.hourlyRate);
      data.append('monthlyRate', formData.monthlyRate);
      data.append('coverLetter', formData.coverLetter);
      data.append('resume', formData.resume);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/applications/send-email`,
        {
          method: 'POST',
          body: data
        }
      );

      const result = await response.json();

      if (response.ok) {
        setMessage('');
        setToastOpen(true);
        setSubmitted(true);
        // Reset form
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          currentLocation: '',
          linkedinUrl: '',
          githubUrl: '',
          yearsOfExperience: '',
          employmentType: {
            fullTime: false,
            partTime: false,
            contract: false
          },
          rateType: 'hourly',
          hourlyRate: '',
          monthlyRate: '',
          coverLetter: '',
          resume: null
        });
      } else {
        setMessage({
          type: 'error',
          text: result.error || 'Failed to submit application'
        });
      }
    } catch (error) {
      setMessage({
        type: 'error',
        text: 'Error submitting application. Please try again.'
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="application-form-container">
      {typeof document !== 'undefined'
        ? createPortal(
            <Fade in={toastOpen} unmountOnExit>
              <div className="application-submit-toast" role="status" aria-live="polite">
                <Alert
                  severity="success"
                  variant="filled"
                  icon={<NotificationsActiveRoundedIcon fontSize="inherit" />}
                  onClose={() => setToastOpen(false)}
                  sx={{
                    width: '100%',
                    alignItems: 'flex-start',
                    backgroundColor: '#07845a',
                    color: '#ffffff',
                    boxShadow: '0 12px 32px rgba(7, 132, 90, 0.28)',
                    borderRadius: '12px',
                    py: 1.25,
                    px: 1.5,
                    '& .MuiAlert-icon': {
                      color: '#ffffff',
                      mt: 0.25,
                      opacity: 0.95,
                    },
                    '& .MuiAlert-message': {
                      color: '#ffffff',
                      paddingTop: 0.25,
                      fontSize: '0.9rem',
                      lineHeight: 1.55,
                    },
                    '& .MuiAlert-action': {
                      pt: 0,
                      pr: 0.25,
                      alignItems: 'flex-start',
                    },
                    '& .MuiIconButton-root': {
                      color: '#ffffff',
                    },
                  }}
                >
                  <AlertTitle
                    sx={{
                      color: '#ffffff',
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      mb: 0.5,
                      letterSpacing: '-0.01em',
                    }}
                  >
                    Application received
                  </AlertTitle>
                  {SUCCESS_TOAST}
                </Alert>
              </div>
            </Fade>,
            document.body
          )
        : null}

      <div className="application-form-card">
        <h1>Apply for Position</h1>
        <p className="form-subtitle">Fill in your information to apply</p>

        {message.text && message.type === 'error' && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Personal Information Section */}
          <div className="form-section">
            <h2>Personal Information</h2>
            
            <div className="form-group">
              <label htmlFor="fullName">Full Name *</label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={handleInputChange}
                placeholder="Enter your full name"
              />
              {errors.fullName && <span className="error">{errors.fullName}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="email">Email *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder="your@email.com"
                />
                {errors.email && <span className="error">{errors.email}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="phone">Phone *</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="+1 (234) 567-8900"
                />
                {errors.phone && <span className="error">{errors.phone}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="currentLocation">Current Location *</label>
              <input
                type="text"
                id="currentLocation"
                name="currentLocation"
                value={formData.currentLocation}
                onChange={handleInputChange}
                placeholder="City, Country"
              />
              {errors.currentLocation && <span className="error">{errors.currentLocation}</span>}
            </div>
          </div>

          {/* Professional Links Section */}
          <div className="form-section">
            <h2>Professional Links</h2>
            
            <div className="form-group">
              <label htmlFor="linkedinUrl">LinkedIn URL *</label>
              <input
                type="url"
                id="linkedinUrl"
                name="linkedinUrl"
                value={formData.linkedinUrl}
                onChange={handleInputChange}
                placeholder="https://linkedin.com/in/yourprofile"
              />
              {errors.linkedinUrl && <span className="error">{errors.linkedinUrl}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="githubUrl">GitHub URL *</label>
              <input
                type="url"
                id="githubUrl"
                name="githubUrl"
                value={formData.githubUrl}
                onChange={handleInputChange}
                placeholder="https://github.com/yourprofile"
              />
              {errors.githubUrl && <span className="error">{errors.githubUrl}</span>}
            </div>
          </div>

          {/* Experience Section */}
          <div className="form-section">
            <h2>Experience & Preferences</h2>
            
            <div className="form-group">
              <label htmlFor="yearsOfExperience">Years of Experience *</label>
              <select
                id="yearsOfExperience"
                name="yearsOfExperience"
                value={formData.yearsOfExperience}
                onChange={handleInputChange}
              >
                <option value="">Select years of experience</option>
                <option value="0-1">0-1 years</option>
                <option value="1-3">1-3 years</option>
                <option value="3-5">3-5 years</option>
                <option value="5-10">5-10 years</option>
                <option value="10+">10+ years</option>
              </select>
              {errors.yearsOfExperience && <span className="error">{errors.yearsOfExperience}</span>}
            </div>

            <div className="form-group">
              <label>Preferred Employment Type (Select at least one)</label>
              <div className="apply-checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.employmentType.fullTime}
                    onChange={() => handleEmploymentTypeChange('fullTime')}
                  />
                  <span>Full-time</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.employmentType.partTime}
                    onChange={() => handleEmploymentTypeChange('partTime')}
                  />
                  <span>Part-time</span>
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.employmentType.contract}
                    onChange={() => handleEmploymentTypeChange('contract')}
                  />
                  <span>Contract</span>
                </label>
              </div>
              {errors.employmentType && <span className="error">{errors.employmentType}</span>}
            </div>
          </div>

          {/* Rate Section */}
          <div className="form-section">
            <h2>Expected Compensation</h2>
            
            <div className="form-group">
              <label htmlFor="hourlyRate">Hourly Rate (USD) *</label>
              <input
                type="number"
                id="hourlyRate"
                name="hourlyRate"
                value={formData.hourlyRate}
                onChange={handleInputChange}
                placeholder="e.g., 50"
                min="0"
                step="1"
              />
              {errors.hourlyRate && <span className="error">{errors.hourlyRate}</span>}
            </div>
          </div>

          {/* Additional Information Section */}
          <div className="form-section">
            <h2>Additional Information</h2>
            
            <div className="form-group">
              <label htmlFor="coverLetter">Cover Letter (Optional)</label>
              <textarea
                id="coverLetter"
                name="coverLetter"
                value={formData.coverLetter}
                onChange={handleInputChange}
                placeholder="Tell us why you're interested in this position..."
                rows="5"
              />
            </div>

            <div className="form-group">
              <label htmlFor="resume">Resume * (PDF, DOC, DOCX - Max 5MB)</label>
              <input
                type="file"
                id="resume"
                accept=".pdf,.doc,.docx"
                onChange={handleResumeChange}
              />
              {formData.resume && (
                <p className="file-info">Selected: {formData.resume.name}</p>
              )}
              {errors.resume && <span className="error">{errors.resume}</span>}
            </div>
          </div>

          {/* Submit Button */}
          <div className="form-actions">
            <button type="submit" className="btn-submit" disabled={submitting || submitted}>
              {submitted ? 'Sent' : submitting ? 'Submitting...' : 'Submit Application'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ApplicationForm;
